/**
 * CRM Object Store — normalized, centralized cache for CRM objects.
 *
 * A consumer of the generic `createRecordStore<CrmObject>` toolkit
 * (`@/lib/store/createRecordStore`): the toolkit owns the Map, snapshot
 * identity, global + per-record subscriptions, seq guard, batch mutation,
 * and size-capped LRU eviction. This module layers CRM-specific concerns on
 * top — merge-alias resolution, the email index, pending
 * creations/associations for optimistic list insertion, and the numeric-PK
 * lookup index.
 *
 * This is a module-level external store (not React Context) because
 * applyEvent.ts writes from within a useCallback — it's not a React
 * component and can't use context.
 *
 * Size-capped LRU eviction: once the store exceeds `STORE_MAX_ENTRIES`
 * entries, it evicts the least-recently-touched (write or subscribe)
 * entries down to the cap (write/subscribe-recency, not TTL — WebSocket
 * keeps entries fresh, so read access doesn't imply relevance). Subscribed
 * (detail/slideout) and optimistic (pending-creation/association) entries
 * are never evicted. An aliased entry is protected only while one of its
 * alias keys still has an active subscriber — an aliased-but-unsubscribed
 * entry is fully evictable like any other. See `subscribeToEviction` for
 * the notification seam consumers (e.g. list sentinel invalidation) can
 * hook into.
 */

import { CONTACT, USER } from '@/lib/crm/core/entityTypes';
import type { EntityTypeName } from '@/lib/crm/core/types';
import type {
  RecordMeta,
  SeqResult,
  StoredRecord,
} from '@/lib/store/createRecordStore';
import { createRecordStore } from '@/lib/store/createRecordStore';
import type { AssociationDetail, CrmObject, PropertyValue } from '@/types/crm';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type { SeqResult };
export type StoredObjectMeta = RecordMeta;
export type StoredObject = StoredRecord<CrmObject>;

/** A record evicted by the toolkit's LRU cap, mapped to CRM's field names. */
export interface EvictedRecord {
  entityId: string;
  objectId: string | number;
  entityTypeName: EntityTypeName;
}

/**
 * Store size above which eviction kicks in. A generous default kept above
 * the existing 10k dev-warning so the warning still fires early; not
 * org-configurable (Key Decision 4).
 */
export const STORE_MAX_ENTRIES = 50_000;

/** Mirrors the toolkit's current cap — the toolkit has no getter, so this
 * lets `_maybeReconcileEvictedCrmState` gate its sweep without one. */
let currentMaxEntries = STORE_MAX_ENTRIES;

// ---------------------------------------------------------------------------
// CRM-specific state
// ---------------------------------------------------------------------------

/** Secondary index: Map<numericPkString, timelineId> for slideout backward compat. */
let pkIndex = new Map<string, string>();

/**
 * Entity-alias index: Map<requestedId, canonicalEntityId>.
 *
 * When a caller asks the batch-fetch endpoint for an entity by its pre-merge
 * ID, the server resolves it to the canonical post-merge object. The store
 * keys everything by the canonical `entity_id`, so without an alias the
 * requested pre-merge ID has no entry and `useStoreObjects([preMergeId])`
 * would see nothing.
 *
 * This alias map lets hooks resolve a requested ID to its canonical
 * entry, so merged association targets resolve in the UI after the fetch
 * returns (and stay resolved as WS events update the canonical entry).
 */
let aliases = new Map<string, string>();

/**
 * Reverse alias index: Map<canonicalId, Set<requestedId>>.
 *
 * Maintained alongside `aliases` so a canonical-id change can efficiently
 * notify subscribers that registered under a pre-merge (requested) ID. This
 * is wired into the toolkit as `extraChangeKeys`, so `store.commit`/
 * `store.remove` on the canonical id automatically fans out to every
 * requested id that resolves to it.
 */
let reverseAliases = new Map<string, Set<string>>();

/**
 * Canonical ids that were confirmed present in the toolkit's store at least
 * once while they had a reverse alias registered against them. Used by
 * `_reconcileEvictedCrmState` to tell "this canonical was fetched, then
 * evicted" (needs alias cleanup) apart from "this canonical was never
 * fetched" (the alias is still a legitimate not-yet-resolved pointer) —
 * both look identical as `!store.getRecord(canonicalId)`.
 */
let confirmedAliasTargets = new Set<string>();

/**
 * Email index: `Map<normalizedEmail, entityId>`.
 *
 * Maintained alongside the store so any consumer that needs "which entity
 * has this email" reads from the store directly (no separate React Query
 * cache, no out-of-band invalidation). The index is updated by:
 *
 *   - {@link upsertObject} — when a full-fetched object is stored, every
 *     value in its `ahoy_email` property is added (or moved) to the index.
 *   - {@link applyPropertyUpdate} — when `ahoy_email` changes, the stale
 *     entries are removed and the new ones added in one pass.
 *   - {@link applyObjectMerged} — every email pointing at the source entity
 *     is re-pointed at the canonical. Subscribers re-render with the new
 *     entity via {@link useCrmObjectSnapshot} (or the equivalent hook).
 *   - {@link applyObjectDeleted} — emails for the deleted entity are removed.
 *
 * `null` value sentinels are NOT stored — absence means "unknown". Callers
 * use `emailIndexNegative` for "we searched and confirmed there's no entity"
 * to avoid repeating a search-API call for known-unresolved addresses.
 */
let emailIndex = new Map<string, string>();

/**
 * Reverse index: `Map<entityId, Set<normalizedEmail>>`. Required so a
 * property update that REPLACES an entity's email array can find the
 * old emails to unindex (the new event payload only carries the new
 * values).
 */
let emailIndexByEntityId = new Map<string, Set<string>>();

/**
 * Negative-result cache: emails we searched against the backend and got
 * "no entity" back. Populated by callers (the hook) when a search returns
 * empty; lets the next render skip re-searching. Cleared on apply events
 * that could create new mappings (object_created with ahoy_email, or
 * property_updated touching ahoy_email).
 */
let emailIndexNegative = new Set<string>();

/** Per-email listeners: Map<normalizedEmail, Set<listener>> */
let emailIndexListeners = new Map<string, Set<() => void>>();

/**
 * Per-email snapshot cache: Map<normalizedEmail, entityId | null>.
 *
 * Cached so `useSyncExternalStore`'s `getSnapshot` returns the same
 * reference on consecutive calls until the entry changes — without this,
 * React would treat every render as "snapshot changed" and infinite-loop.
 * The sentinel `null` is used for negative-cache hits so the snapshot is
 * stable across renders too.
 */
let emailIndexSnapshots = new Map<string, string | null | undefined>();

/** Prevents duplicate store-size warnings per org session. */
let sizeWarnEmitted = false;

/**
 * Monotonic counter bumped every time the store is wiped via `clearAll()`
 * (e.g., on org switch). Async writers snapshot this value and compare it
 * before applying their results, so stale data from a previous org is
 * never merged into the now-cleared store. See `refreshStoreEntities` in
 * the entity fetch batcher.
 */
let storeGeneration = 0;

/**
 * Ids with at least one active CRM-level subscription (refcounted),
 * mirrored alongside the toolkit's own per-record listener bookkeeping.
 * Needed so `_isProtectedCrm` can protect a record whose ALIAS KEY (not the
 * canonical id itself) has a live subscriber — a case the toolkit's generic
 * `isProtected` can't see, since alias-key subscriptions are opaque to it
 * beyond the notification fanout (`extraChangeKeys`).
 */
let subscribedIds = new Map<string, number>();

/**
 * Predicates registered by preview/overlay providers to identify ids that
 * exist only as an overlay (never a real, upsertable entity) — e.g. the
 * synthetic id a `create` preview overlay assigns before the real entity
 * exists. Checked by {@link isPreviewOnlyId}, consulted at the top of
 * {@link upsertObject} to refuse writing such ids into the store (a DEV-only
 * poisoning guard — see the call site for details).
 */
let previewOnlyIdGuards = new Set<(entityId: string) => boolean>();

/**
 * Register a predicate that identifies preview-only ids. Returns an
 * unregister function; call it on provider teardown so a guard doesn't
 * outlive the overlay state it reads from.
 */
export function registerPreviewOnlyIdGuard(
  fn: (entityId: string) => boolean
): () => void {
  previewOnlyIdGuards.add(fn);
  return () => {
    previewOnlyIdGuards.delete(fn);
  };
}

/** True if any registered guard identifies `entityId` as preview-only. */
function isPreviewOnlyId(entityId: string): boolean {
  for (const guard of previewOnlyIdGuards) {
    if (guard(entityId)) {
      return true;
    }
  }
  return false;
}

function _trackSubscribe(id: string): () => void {
  subscribedIds.set(id, (subscribedIds.get(id) ?? 0) + 1);
  let released = false;
  return () => {
    if (released) {
      return;
    }
    released = true;
    const count = (subscribedIds.get(id) ?? 1) - 1;
    if (count <= 0) {
      subscribedIds.delete(id);
    } else {
      subscribedIds.set(id, count);
    }
  };
}

// ---------------------------------------------------------------------------
// Pending creations — optimistic list insertion for newly created objects
// ---------------------------------------------------------------------------
//
// When a WS `ahoy_object_created` event arrives, we insert the object into
// the store immediately. But the list hook only displays objects from the
// React Query response — the store only *enriches* existing results. So a
// new object won't appear until the list refetches.
//
// To bridge this gap, we track "pending creations" — entity IDs of objects
// that were just created via WS events but haven't yet appeared in a list
// refetch. The list hook injects these at the top of results for instant
// visibility. They're cleared when the next list refetch completes.
// ---------------------------------------------------------------------------

/** Entity IDs of recently created objects not yet confirmed by a list fetch. */
let pendingCreations = new Set<string>();
let pendingCreationsSnapshot = pendingCreations;
let pendingCreationsListeners = new Set<() => void>();

/** Maps entityId → active expiry timer, so we can cancel when a creation is confirmed. */
let pendingCreationTimers = new Map<string, ReturnType<typeof setTimeout>>();

/**
 * Track a newly created object for optimistic list insertion.
 * The object should already be in the main store (via applyPropertyUpdate).
 */
export function addPendingCreation(entityId: string): void {
  pendingCreations = new Set(pendingCreations);
  pendingCreations.add(entityId);
  pendingCreationsSnapshot = pendingCreations;
  emitPendingCreationsChange();
  // Cancel any existing timer for this entity (e.g. re-created after expiry),
  // then schedule auto-cleanup in case the creation is never confirmed by a list refetch.
  const existingTimer = pendingCreationTimers.get(entityId);
  if (existingTimer !== undefined) {
    clearTimeout(existingTimer);
  }
  const timer = setTimeout(() => {
    pendingCreationTimers.delete(entityId);
    console.warn(
      `[crmObjectStore] Pending creation orphaned (not confirmed by list refetch)`,
      { entityId }
    );
    clearPendingCreations([entityId]);
  }, 60_000);
  // Allow Node.js (Vitest fork workers) to exit even if this timer is pending.
  // The timer is only a safety net; it must not keep fork processes alive after tests.
  (timer as ReturnType<typeof setTimeout> & { unref?: () => void }).unref?.();
  pendingCreationTimers.set(entityId, timer);
}

/**
 * Clear specific pending creation IDs.
 * Called after a list refetch confirms those objects exist on the server.
 */
export function clearPendingCreations(entityIds: string[]): void {
  for (const entityId of entityIds) {
    const timer = pendingCreationTimers.get(entityId);
    if (timer !== undefined) {
      clearTimeout(timer);
      pendingCreationTimers.delete(entityId);
    }
  }
  if (entityIds.length === 0) {
    return;
  }
  const toRemove = new Set(entityIds);
  let changed = false;
  const newSet = new Set<string>();
  const clearedIds: string[] = [];
  for (const entityId of pendingCreations) {
    if (toRemove.has(entityId)) {
      changed = true;
      clearedIds.push(entityId);
    } else {
      newSet.add(entityId);
    }
  }
  if (!changed) {
    return;
  }
  pendingCreations = newSet;
  pendingCreationsSnapshot = pendingCreations;
  // Clean up associations for cleared pending creations
  if (clearedIds.length > 0) {
    pendingAssociations = new Map(pendingAssociations);
    for (const id of clearedIds) {
      pendingAssociations.delete(id);
    }
  }
  emitPendingCreationsChange();
}

/** Subscribe to pending creation changes (for useSyncExternalStore). */
export function subscribeToPendingCreations(listener: () => void): () => void {
  pendingCreationsListeners.add(listener);
  return () => pendingCreationsListeners.delete(listener);
}

/** Get the current pending creations snapshot (for useSyncExternalStore). */
export function getPendingCreationsSnapshot(): ReadonlySet<string> {
  return pendingCreationsSnapshot;
}

function emitPendingCreationsChange(): void {
  for (const listener of pendingCreationsListeners) {
    listener();
  }
}

/**
 * Association targets for pending creations.
 *
 * When a new object is created with associations (e.g., a note associated
 * with a contact), this maps the created entity ID to the set of entity IDs
 * it's associated with. This lets hooks that filter by
 * `associated_with_entity_id` know which pending creations to inject.
 */
let pendingAssociations = new Map<string, Set<string>>();

/**
 * Track an association for a pending creation.
 * Call this alongside addPendingCreation when the creation batch includes
 * association events.
 */
export function addPendingAssociation(
  sourceEntityId: string,
  targetEntityId: string
): void {
  const existing = pendingAssociations.get(sourceEntityId);
  if (existing?.has(targetEntityId)) {
    return; // Already indexed — no change, no notification needed.
  }
  pendingAssociations = new Map(pendingAssociations);
  const targets = new Set(existing ?? []);
  targets.add(targetEntityId);
  pendingAssociations.set(sourceEntityId, targets);
  // Notify pending-creation subscribers so `associated_with_entity_id` filtered
  // lists recompute, exactly as removePendingAssociation does. The
  // pending-creation set itself is unchanged, so without this nudge the
  // `useSyncExternalStore` snapshot reference stays identical and a list
  // filtered by a newly-added association won't inject the already-pending
  // creation until another unrelated store event or a refetch.
  emitPendingCreationsChange();
}

/**
 * Remove a tracked association for a pending creation.
 *
 * No-op when the source isn't a tracked pending creation or the target was
 * never added. Keeps `pendingAssociations` consistent when an association
 * is removed before the pending-creation entry is cleared, so list hooks
 * that filter by `associated_with_entity_id` stop injecting the entity
 * under the removed association immediately.
 */
export function removePendingAssociation(
  sourceEntityId: string,
  targetEntityId: string
): void {
  const existing = pendingAssociations.get(sourceEntityId);
  if (!existing || !existing.has(targetEntityId)) {
    return;
  }
  pendingAssociations = new Map(pendingAssociations);
  const targets = new Set(existing);
  targets.delete(targetEntityId);
  if (targets.size === 0) {
    pendingAssociations.delete(sourceEntityId);
  } else {
    pendingAssociations.set(sourceEntityId, targets);
  }
  // Notify pending-creation subscribers so `associated_with_entity_id`
  // filtered lists recompute. The pending-creation set itself is unchanged,
  // so without this nudge the `useSyncExternalStore` snapshot reference stays
  // identical and the filtered list keeps injecting the entity under the
  // removed association.
  emitPendingCreationsChange();
}

/**
 * Get the set of entity IDs a pending creation is associated with.
 */
export function getPendingAssociationTargets(
  entityId: string
): ReadonlySet<string> | undefined {
  return pendingAssociations.get(entityId);
}

// ---------------------------------------------------------------------------
// Batch mutation support (CRM-side bookkeeping layered on the toolkit's own)
// ---------------------------------------------------------------------------
//
// The toolkit's `beginBatch`/`endBatch` handle nesting, the Map swap, and
// notification deferral for `objects`/`objectSnapshots`. CRM's wrapper
// mirrors that same nesting (driven by `store.isBatching()`, so it can
// never drift from the toolkit's own depth) to additionally batch the
// numeric-PK index and defer per-email notifications.
// ---------------------------------------------------------------------------

/** Working copy of `pkIndex` during a batch — mutated in place. */
let batchPkIndex: Map<string, string> | null = null;

/**
 * Normalized email addresses whose index entry changed during the current
 * batch. Per-email subscribers (`subscribeToEmailIndex`) are notified for
 * each of these once at endBatch, mirroring how per-object subscribers are
 * flushed via the toolkit's own batch. Without this, callers watching an
 * email (e.g. EntityBadgeByEmail) would not re-resolve after a websocket
 * burst.
 */
let batchChangedEmails: Set<string> = new Set();

/**
 * Begin a batch mutation. Multiple writes between beginBatch/endBatch
 * share a single Map copy and emit a single notification at the end.
 *
 * Batches can be nested — only the outermost endBatch commits.
 *
 * Usage (from applyEvent.ts):
 * ```ts
 * beginBatch();
 * try {
 *   for (const event of events) applyPropertyUpdate(...);
 * } finally {
 *   endBatch();
 * }
 * ```
 */
export function beginBatch(): void {
  if (!store.isBatching()) {
    batchPkIndex = new Map(pkIndex);
    batchChangedEmails = new Set();
  }
  store.beginBatch();
}

/**
 * End a batch mutation. When the outermost batch ends, promotes working
 * copies to module state and emits all deferred notifications. Object
 * notifications flush first (via the toolkit's own `endBatch`), then
 * per-email notifications — matching the original ordering.
 */
export function endBatch(): void {
  store.endBatch();
  if (store.isBatching()) {
    return; // Still nested — wait for the outermost call.
  }

  if (!sizeWarnEmitted && store.getSnapshot().size > 10_000) {
    sizeWarnEmitted = true;
    console.warn(
      `[crmObjectStore] Store size exceeded 10,000 entries (current: ${store.getSnapshot().size}). ` +
        'Consider calling clearAll() on org switch to prevent memory buildup.'
    );
  }

  if (batchPkIndex) {
    pkIndex = batchPkIndex;
  }
  batchPkIndex = null;

  if (batchChangedEmails.size > 0) {
    for (const email of batchChangedEmails) {
      emitEmailIndexChange(email);
    }
  }
  batchChangedEmails = new Set();

  _maybeReconcileEvictedCrmState();
}

// ---------------------------------------------------------------------------
// LRU eviction — the generic engine lives in the toolkit; this layers on
// CRM-specific protection and cleanup.
// ---------------------------------------------------------------------------

/**
 * Whether `id` should never be evicted beyond what the toolkit itself
 * already protects (an id with an active `store.subscribeToRecord`
 * listener): an unconfirmed optimistic creation, the source or target of a
 * pending optimistic association, or an id whose alias key has an active
 * subscriber.
 */
function _isProtectedCrm(id: string): boolean {
  if (pendingCreations.has(id)) {
    return true;
  }
  if (pendingAssociations.has(id)) {
    return true;
  }
  for (const targets of pendingAssociations.values()) {
    if (targets.has(id)) {
      return true;
    }
  }
  const aliasKeys = reverseAliases.get(id);
  if (aliasKeys) {
    for (const k of aliasKeys) {
      if (subscribedIds.has(k)) {
        return true;
      }
    }
  }
  return false;
}

const store = createRecordStore<CrmObject>({
  extraChangeKeys: id => reverseAliases.get(id) ?? [],
  isProtected: _isProtectedCrm,
  maxEntries: STORE_MAX_ENTRIES,
});

/**
 * The toolkit evicts records from its own Map synchronously (inside
 * `store.commit`/`store.endBatch`), but only notifies `subscribeToEviction`
 * listeners on a `queueMicrotask`. CRM-side indexes (pkIndex, aliases, the
 * email index) must reflect an eviction synchronously — `resolveEntityId`
 * and `getEmailIndexSnapshot` must stop resolving to an evicted id
 * immediately, not after a microtask tick — so this sweep runs right after
 * any write that could have triggered eviction and drops any CRM-side entry
 * whose target record is no longer in the toolkit's store.
 *
 * Only entries confirmed present at some point are swept (see
 * `confirmedAliasTargets`) — an alias registered for a canonical id that
 * hasn't been fetched yet is a legitimate, not-yet-resolved pointer, not an
 * eviction victim, even though both look like `!store.getRecord(id)`.
 */
function _reconcileEvictedCrmState(): void {
  for (const [pk, id] of pkIndex) {
    if (!store.getRecord(id)) {
      pkIndex = new Map(pkIndex);
      pkIndex.delete(pk);
    }
  }
  for (const [canonicalId, requestedIds] of reverseAliases) {
    if (
      confirmedAliasTargets.has(canonicalId) &&
      !store.getRecord(canonicalId)
    ) {
      aliases = new Map(aliases);
      reverseAliases = new Map(reverseAliases);
      for (const requestedId of requestedIds) {
        aliases.delete(requestedId);
      }
      reverseAliases.delete(canonicalId);
      confirmedAliasTargets.delete(canonicalId);
    }
  }
  for (const entityId of [...emailIndexByEntityId.keys()]) {
    if (!store.getRecord(entityId)) {
      _unindexEntityEmails(entityId);
    }
  }
}

/**
 * Eviction only ever happens once the store exceeds its cap, and it always
 * brings the size back down to at-or-below the cap. So whenever the store
 * is comfortably under the cap, no eviction has occurred and the sweep
 * above has nothing to do — this gate skips it in that (overwhelmingly
 * common) case instead of walking the pkIndex/alias/email maps on every
 * write.
 */
function _maybeReconcileEvictedCrmState(): void {
  if (!store.isBatching() && store.getSnapshot().size >= currentMaxEntries) {
    _reconcileEvictedCrmState();
  }
}

/**
 * Subscribe to eviction notifications. The listener is invoked (via
 * `queueMicrotask`, never synchronously) with the records evicted by a pass.
 */
export function subscribeToEviction(
  cb: (evicted: EvictedRecord[]) => void
): () => void {
  return store.subscribeToEviction(evicted => {
    cb(
      evicted.map(rec => ({
        entityId: rec.id,
        objectId: rec.data.id,
        entityTypeName: rec.data.entity_type_name,
      }))
    );
  });
}

// ---------------------------------------------------------------------------
// Reads
// ---------------------------------------------------------------------------

/**
 * Get a single object from the store by timeline_id (UUID).
 */
export function getObject(timelineId: string): CrmObject | undefined {
  return store.getData(timelineId);
}

/**
 * Get the full StoredObject (data + meta) by timeline_id.
 *
 * Use this when callers need the meta fields (lastSeq, fetchedAt, isComplete)
 * — for example, to capture a pre-mutation snapshot that can later be restored
 * via restoreStoredObject without losing the original completeness state.
 */
export function getStoredObject(timelineId: string): StoredObject | undefined {
  return store.getRecord(timelineId);
}

/**
 * Resolve a requested entity id to its canonical store key, following
 * the merge-alias map. Returns the input unchanged when no alias exists.
 */
export function resolveEntityId(requestedId: string): string {
  let id = requestedId;
  let hops = 0;
  while (hops < 10) {
    const next = aliases.get(id);
    if (!next) {
      return id;
    }
    id = next;
    hops++;
  }
  // Cycle or unexpectedly long chain — log and return best-known id
  console.warn('[CRM store] resolveEntityId: alias chain exceeded 10 hops', {
    requestedId,
    resolvedId: id,
  });
  return id;
}

/**
 * Register an alias from a pre-merge (requested) entity id to the canonical
 * post-merge entity id. Idempotent — a no-op when the alias already exists
 * or when the two ids are the same.
 */
export function registerEntityAlias(
  requestedId: string,
  canonicalId: string
): void {
  if (requestedId === canonicalId) {
    return;
  }
  if (aliases.get(requestedId) === canonicalId) {
    return;
  }
  aliases = new Map(aliases);
  aliases.set(requestedId, canonicalId);

  // Maintain the reverse index for subscriber notification.
  reverseAliases = new Map(reverseAliases);
  let reverseSet = reverseAliases.get(canonicalId);
  if (!reverseSet) {
    reverseSet = new Set();
    reverseAliases.set(canonicalId, reverseSet);
  } else {
    reverseSet = new Set(reverseSet);
    reverseAliases.set(canonicalId, reverseSet);
  }
  reverseSet.add(requestedId);

  // Inherit all transitive reverse aliases from requestedId so that a
  // canonical-id change notifies subscribers at every hop.
  const transitiveReverseSet = reverseAliases.get(requestedId);
  if (transitiveReverseSet) {
    for (const transitiveId of transitiveReverseSet) {
      reverseSet.add(transitiveId);
    }
  }

  // Notify unconditionally: when the canonical record is already in the
  // store, re-committing it fires both the toolkit's global change (so
  // generic subscribers re-resolve via `resolveEntityId`) and its
  // per-record change — which, via `extraChangeKeys`, fans out to every id
  // in `reverseAliases` (including the one just registered above). When the
  // canonical hasn't been fetched yet there's no data to re-commit, but
  // subscribers registered under the pre-merge id (e.g. via
  // `resolveEntityId`-dependent consumers) still need to re-render now that
  // the alias exists — `notifyRecord` fires the same fan-out without
  // requiring committed data.
  const canonicalRecord = store.getRecord(canonicalId);
  if (canonicalRecord) {
    confirmedAliasTargets.add(canonicalId);
    store.commit(canonicalId, canonicalRecord);
  } else {
    store.notifyRecord(canonicalId);
  }
}

// ---------------------------------------------------------------------------
// Email index — public surface
// ---------------------------------------------------------------------------

/**
 * Sentinel returned by `getEmailIndexSnapshot` when the store has neither a
 * positive mapping nor a negative-cache hit. Distinguished from `null` so
 * callers can tell "unknown — fetch needed" apart from "known unresolved".
 */
export const EMAIL_INDEX_UNKNOWN: unique symbol = Symbol('EMAIL_INDEX_UNKNOWN');

export type EmailIndexSnapshot =
  | string // entity_id (canonical post-alias resolution)
  | null // confirmed-unresolved by a prior search
  | typeof EMAIL_INDEX_UNKNOWN; // never asked; caller should fetch

/**
 * Synchronous lookup: returns the entity_id for an email, the negative
 * sentinel if the caller has previously recorded "no entity exists", or
 * `EMAIL_INDEX_UNKNOWN` if neither.
 *
 * Resolves through `aliases` so a merged source's email returns the
 * canonical entity id directly.
 */
export function getEmailIndexSnapshot(email: string): EmailIndexSnapshot {
  const normalized = _normalizeEmail(email);
  if (!normalized) {
    return null;
  }
  // Use `.has()` to distinguish "key absent" from "cached as
  // EMAIL_INDEX_UNKNOWN" — both shapes look like `undefined` from
  // `.get()`, so a `get(...) !== undefined` check would never hit the
  // unknown-sentinel branch (every UNKNOWN lookup would recompute).
  if (emailIndexSnapshots.has(normalized)) {
    const cached = emailIndexSnapshots.get(normalized);
    return cached === undefined ? EMAIL_INDEX_UNKNOWN : cached;
  }

  let resolved: EmailIndexSnapshot;
  const direct = emailIndex.get(normalized);
  if (direct) {
    resolved = aliases.get(direct) ?? direct;
  } else if (emailIndexNegative.has(normalized)) {
    resolved = null;
  } else {
    resolved = EMAIL_INDEX_UNKNOWN;
  }

  // Cache so `useSyncExternalStore` sees a stable reference between renders.
  // `EMAIL_INDEX_UNKNOWN` is stored via the sentinel; null/string are stored
  // as-is. We use `undefined` as the "not cached" sentinel internally.
  emailIndexSnapshots.set(
    normalized,
    resolved === EMAIL_INDEX_UNKNOWN ? undefined : resolved
  );
  return resolved;
}

/**
 * Subscribe to email-index changes for a single normalized email. Pair with
 * `useSyncExternalStore` to get a React-friendly hook.
 */
export function subscribeToEmailIndex(
  email: string,
  listener: () => void
): () => void {
  const normalized = _normalizeEmail(email);
  if (!normalized) {
    return () => {};
  }
  let set = emailIndexListeners.get(normalized);
  if (!set) {
    set = new Set();
    emailIndexListeners.set(normalized, set);
  }
  set.add(listener);
  return () => {
    const s = emailIndexListeners.get(normalized);
    if (!s) {
      return;
    }
    s.delete(listener);
    if (s.size === 0) {
      emailIndexListeners.delete(normalized);
    }
  };
}

/**
 * Record a negative search result so subsequent renders skip a re-fetch.
 * Cleared automatically when any apply event surfaces an entity with this
 * email.
 */
export function markEmailIndexUnresolved(email: string): void {
  const normalized = _normalizeEmail(email);
  if (!normalized) {
    return;
  }
  if (emailIndex.has(normalized)) {
    // A positive mapping was added between the search firing and the result
    // arriving — keep the positive value, ignore the stale negative.
    return;
  }
  if (emailIndexNegative.has(normalized)) {
    return;
  }
  emailIndexNegative.add(normalized);
  emailIndexSnapshots.delete(normalized);
  emitEmailIndexChange(normalized);
}

/**
 * Register a server-confirmed email→entity resolution in the email index,
 * independent of the entity's stored `ahoy_email` property value.
 *
 * Needed by the search-resolution seed path (`usePersonByEmail`): a
 * timeline/batch response can hold the entity in the store at a newer seq
 * with a lean keyed-but-EMPTY `ahoy_email`, so the seed upsert's older-seq
 * merge keeps the empty value and `_reindexEntityEmails` never registers
 * the address — leaving the subscriber in a permanent loading state even
 * though the server just confirmed the mapping. This entrypoint registers
 * the mapping through the same machinery (`_reindexEntityEmails`), so the
 * entity-type collision rank, negative-cache clearing, snapshot
 * invalidation, and batch-aware notification all apply unchanged. The
 * union with already-registered emails makes the removal arm a no-op —
 * this is strictly additive.
 *
 * It deliberately does NOT touch the entity's property data: an empty
 * stored `ahoy_email` may be legitimate newer state (the value cleared by
 * a WS-applied event); only the resolution index is updated.
 */
export function registerResolvedEmail(
  entityId: string,
  entityTypeName: string | undefined,
  email: string
): void {
  const normalized = _normalizeEmail(email);
  if (!normalized) {
    return;
  }
  const registered = emailIndexByEntityId.get(entityId) ?? new Set<string>();
  const union = new Set(registered);
  union.add(normalized);
  _reindexEntityEmails(entityId, entityTypeName, [...union]);
}

/**
 * Get a single object by numeric PK string (for slideout backward compat).
 * Looks up the PK index, then reads from the appropriate source.
 * Batch-aware: reads from batchPkIndex during a batch.
 */
export function getObjectByPk(pk: string): CrmObject | undefined {
  const index = store.isBatching() ? batchPkIndex : pkIndex;
  if (!index) {
    throw new Error('Batch state inconsistency');
  }
  const timelineId = index.get(pk);
  if (!timelineId) {
    return undefined;
  }
  return store.getData(timelineId);
}

/**
 * Get the seq number for a stored object (for dedup checks).
 * Batch-aware: reads from the in-progress batch during a batch.
 */
export function getObjectSeq(timelineId: string): number | undefined {
  return store.getSeq(timelineId);
}

/**
 * Return the exact AssociationDetail object reference the store currently holds
 * for a (type, target) pair on an entity, or undefined if the pair is absent.
 *
 * Batch-aware, so an optimistic writer can call it right after
 * `applyAssociationChange` to capture the reference its write produced. Rollback
 * uses reference identity on that object to tell "our optimistic add is still
 * live" apart from "a concurrent authoritative event re-asserted this pair" — an
 * authoritative apply (WS delivery, via `forceNewReference`) replaces the element
 * with a new reference for both a remove+re-add and an idempotent re-add of an
 * already-present pair, so the final presence boolean alone cannot distinguish
 * the two.
 */
export function peekAssociationDetail(
  timelineId: string,
  associationTypeName: string,
  targetEntityId: string
): AssociationDetail | undefined {
  return store
    .getData(timelineId)
    ?.associations?.find(
      a =>
        a.association_type_name === associationTypeName &&
        a.target_entity_id === targetEntityId
    );
}

/**
 * Check if a complete (server-fetched) version of the object exists.
 */
export function hasCompleteObject(timelineId: string): boolean {
  return store.hasComplete(resolveEntityId(timelineId));
}

/**
 * Return the canonical entity IDs of every *complete* (full-fetched) object
 * currently in the store.
 *
 * Used by the WebSocket full-refresh recovery path to re-fetch store-backed
 * objects (entity badges, object cards hydrated via `ensureEntity` rather than
 * a React Query observer). Query invalidation alone never refreshes those, so
 * without an explicit re-fetch they keep stale or deleted data indefinitely
 * after missed-event recovery.
 *
 * Summary/partial entries (isComplete: false) are excluded — they already fall
 * back to React Query data, which the recovery path invalidates separately.
 */
export function getCompleteObjectIds(): string[] {
  return store.getCompleteIds();
}

/**
 * Current store generation. Incremented by `clearAll()` on every org-lifecycle
 * wipe (e.g. org switch). Async writers snapshot this before awaiting and
 * re-check it before merging, so results computed under a previous org are
 * dropped instead of leaking cross-org data into the cleared store.
 */
export function getStoreGeneration(): number {
  return storeGeneration;
}

// ---------------------------------------------------------------------------
// useSyncExternalStore — global
// ---------------------------------------------------------------------------

/**
 * Subscribe to any store change. Returns an unsubscribe function.
 */
export function subscribe(listener: () => void): () => void {
  return store.subscribe(listener);
}

/**
 * Get the current store snapshot. Returns a stable reference that only
 * changes when the store is mutated.
 */
export function getSnapshot(): ReadonlyMap<string, StoredObject> {
  return store.getSnapshot();
}

// ---------------------------------------------------------------------------
// useSyncExternalStore — per-object
// ---------------------------------------------------------------------------

/**
 * Create a per-object subscription. The returned subscribe/getSnapshot pair
 * only fires when that specific object changes, avoiding re-renders of
 * unrelated detail/slideout views.
 *
 * Usage:
 * ```tsx
 * const { subscribe, getSnapshot } = subscribeToObject(timelineId);
 * const object = useSyncExternalStore(subscribe, getSnapshot);
 * ```
 */
export function subscribeToObject(timelineId: string): {
  subscribe: (listener: () => void) => () => void;
  getSnapshot: () => CrmObject | undefined;
} {
  return {
    subscribe: (listener: () => void) => {
      const untrack = _trackSubscribe(timelineId);
      const unsubscribe = store
        .subscribeToRecord(timelineId)
        .subscribe(listener);
      return () => {
        unsubscribe();
        untrack();
      };
    },
    getSnapshot: () => {
      // Resolve through the merge-alias map so subscribers using a pre-merge
      // entity id transparently receive the canonical post-merge object.
      // Only return data for complete entries. Incomplete entries (created by
      // WS events for objects we haven't fetched yet) have missing fields and
      // would corrupt detail/slideout rendering. Returning undefined lets
      // consumers fall back to React Query data via `storeObject ?? queryData`.
      const rec = store.getRecord(resolveEntityId(timelineId));
      return rec?.meta.isComplete ? rec.data : undefined;
    },
  };
}

/**
 * Per-object subscription that returns the full StoredObject (data + meta)
 * regardless of completeness.
 *
 * Use this when the caller wants to render a placeholder from a summary
 * (incomplete) entry while a full fetch is in flight, then upgrade to the
 * complete view once `meta.isComplete` flips to `true`. The slideout uses
 * this to avoid the cold-fetch record-skeleton flash for rows that are
 * already known to the store via the table's list response.
 *
 * Unlike `subscribeToObject` which silently returns `undefined` for summary
 * entries, this surface returns the entry and lets the caller decide how to
 * render based on `meta.isComplete`. Consumers MUST check `meta.isComplete`
 * before rendering body sections that assume a full property set — most
 * sections will render incorrectly with summary data and should show
 * section-level skeletons until the fetch resolves.
 */
export function subscribeToStoredObject(timelineId: string): {
  subscribe: (listener: () => void) => () => void;
  getSnapshot: () => StoredObject | undefined;
} {
  return {
    subscribe: (listener: () => void) => {
      const untrack = _trackSubscribe(timelineId);
      const unsubscribe = store
        .subscribeToRecord(timelineId)
        .subscribe(listener);
      return () => {
        unsubscribe();
        untrack();
      };
    },
    getSnapshot: () => {
      // Resolve through the merge-alias map so subscribers using a pre-merge
      // entity id transparently receive the canonical post-merge object.
      return store.getRecord(resolveEntityId(timelineId));
    },
  };
}

// ---------------------------------------------------------------------------
// Writes — from server fetches
// ---------------------------------------------------------------------------

/**
 * Merge an incoming associations array with the existing one, honoring an
 * optional projection of association types the incoming payload is
 * authoritative for.
 *
 * `projectedTypes === null` means the incoming payload is authoritative for
 * the complete associations set (the default, unprojected case) — replace
 * wholesale. When a projection is supplied, the incoming payload is only
 * authoritative for those types: keep existing entries whose type falls
 * OUTSIDE the projection, then append the incoming entries. This is a
 * filter-then-concat, not a union, so a removal INSIDE the projection still
 * takes effect.
 */
function applyAssociationProjection(
  existingAssociations: AssociationDetail[] | undefined,
  incomingAssociations: AssociationDetail[],
  projectedTypes: Set<string> | null
): AssociationDetail[] {
  if (!projectedTypes) {
    return incomingAssociations;
  }
  const preserved = (existingAssociations ?? []).filter(
    association => !projectedTypes.has(association.association_type_name)
  );
  return [...preserved, ...incomingAssociations];
}

function toProjectedTypesSet(
  projectedTypes: string[] | null | undefined
): Set<string> | null {
  return projectedTypes ? new Set(projectedTypes) : null;
}

/**
 * Guarantee the store's `CrmObject.id` invariant on an incoming payload.
 *
 * `id` and `entity_id` carry the same value on every payload that includes
 * both — `CrmObjectSerializer` maps `id` from `entity.entity_id`. But not
 * every endpoint includes both: `/crm-events/entities/{id}/` (the slideout's
 * detail fetch) deliberately omits `id`, treating it as a legacy field the
 * newer endpoint declines to propagate — a decision pinned by
 * `test_entity_retrieve.py::test_response_does_not_include_legacy_id_field`.
 * So its payloads arrive with `id === undefined` despite `CrmObject['id']`
 * being declared non-optional, and because every merge path below spreads the
 * incoming payload over the stored entry, an absent `id` doesn't merely fail
 * to arrive — it overwrites a good stored one.
 *
 * Two failures that caused, both live before this guard:
 *   - `PipelineBoard` keys and dedupes cards on `String(getItemId(item))`.
 *     Two objects both stringifying to `'undefined'` collapse into one
 *     draggable identity and the second card silently vanishes from the board.
 *   - `updatePkIndex` maps `String(obj.id) → entity_id` whenever the two
 *     differ, so each such upsert wrote a junk `'undefined'` key that the next
 *     one overwrote.
 *
 * Normalizing here rather than widening the wire keeps that endpoint's
 * decision intact and puts the fix in the same place as the `associations ??`
 * / `associated_objects ??` guards below — the store is the one component
 * that already knows detail payloads are leaner than list payloads, and
 * fixing it here covers every consumer that keys on `id`.
 *
 * Only ever fills in a missing value, and prefers the id already stored over
 * `entity_id`: the two differ precisely when the stored entry carries a
 * numeric PK, which is what `updatePkIndex` indexes — filling from `entity_id`
 * there would silently drop the PK the index points at. `entity_id` is the
 * fallback for a first write with nothing to preserve.
 */
function withGuaranteedId(obj: CrmObject, existingId?: string): CrmObject {
  if (obj.id !== undefined && obj.id !== null) {
    return obj;
  }
  return { ...obj, id: existingId ?? obj.entity_id };
}

/**
 * Upsert a complete CrmObject from a server fetch.
 *
 * Server fetches are authoritative for the full object shape (all properties,
 * associations, metadata). When the store has a newer seq (from WS events
 * that arrived after the fetch started), we merge: the server response
 * provides the complete base, and any WS-applied property updates are
 * overlaid on top. This ensures we never lose properties from a full fetch
 * just because a WS event bumped the seq.
 *
 * Beyond merging the object body, this also updates the PK index and email index
 * for `obj` in the same call — these are not separable; do not modify only the
 * merge logic without checking both index updates below.
 */
export function upsertObject(incoming: CrmObject): void {
  if (import.meta.env.DEV && isPreviewOnlyId(incoming.entity_id)) {
    console.error(
      `[crmObjectStore] Refusing to upsert preview-only entity ${incoming.entity_id}: ` +
        'projected/overlay objects must never enter the store (poisoning hazard). ' +
        'The create overlay must be torn down before the real entity is upserted.'
    );
    return;
  }
  const existing = store.getRecord(incoming.entity_id);
  const obj = withGuaranteedId(incoming, existing?.data.id);

  let mergedData: CrmObject;
  let mergedSeq: number;

  if (existing && obj.last_event_seq < existing.meta.lastSeq) {
    // Store has newer seq from WS events. Merge: use the full server
    // response as the base (it has all properties), then overlay any
    // property updates the store accumulated from WS events. Existing's
    // associations also reflect WS-applied changes
    // (`applyAssociationChange`) that this older fetch hasn't seen, so
    // existing wins outright on associations in this branch.
    mergedData = {
      ...obj,
      associations: existing.data.associations,
      associated_objects: existing.data.associated_objects,
      properties: { ...obj.properties, ...existing.data.properties },
      last_event_seq: existing.meta.lastSeq,
    };
    mergedSeq = existing.meta.lastSeq;
  } else if (existing) {
    // Always merge with the existing entry — preserve any properties the
    // existing entry has that the new response doesn't include, and let
    // the new response's properties win on overlap. This protects the
    // store against TWO classes of leaner responses:
    //
    //   1. List-only computed/joined display properties (last_activity,
    //      aggregated counts) seeded by `upsertSummaryObjects` from a
    //      list response — `/entities/{id}/` may not include them, and
    //      replacing wholesale would clear them from the store, visibly
    //      blanking out the corresponding table column.
    //
    //   2. Cross-entity `associated_entities` payloads. When a slideout
    //      opens for entity X, `X.queryFn` returns `X.associated_entities`
    //      which can include OTHER entities the user has previously
    //      detail-fetched (e.g. clicking the company on a contact's
    //      slideout, where that contact is now in the company's
    //      associated_entities). The backend strips `associations` from
    //      these payloads via `skip_associations=True`
    //      (apps/crm_events/serializers.py:213) — an intentional perf
    //      decision for high-degree entities — so `obj.associations` is
    //      `undefined` in those payloads. Without the `??` guard below,
    //      the spread would overwrite existing's array with undefined
    //      and association-derived columns in the table behind the
    //      slideout would visibly clear. Empty array `[]` (a genuinely
    //      cleared associations set) is truthy and correctly wins.
    //
    // Merging preserves both classes. Cleared properties are handled
    // correctly because the backend returns `null` for cleared fields
    // (vs omitting them), and a `null` value in the new response
    // overrides the old value via the standard `{ ...old, ...new }`
    // overlay semantics.
    mergedData = {
      ...obj,
      associations: obj.associations ?? existing.data.associations,
      // associated_objects: the backend only ever sets this key on the
      // response when the caller opted in (`include_associated_objects=true`
      // or an `include_associations_config`); an unrequested response omits
      // the key entirely (`undefined`). So a *present* value — even `{}` for
      // an entity with genuinely zero associations — is an authoritative
      // fetch result, not "didn't fetch". Only `undefined` means "this
      // payload didn't opt in", in which case preserve any previously-
      // hydrated overlay.
      associated_objects:
        obj.associated_objects ?? existing.data.associated_objects,
      properties: { ...existing.data.properties, ...obj.properties },
    };
    mergedSeq = obj.last_event_seq;
  } else {
    // No existing entry — use new directly.
    mergedData = obj;
    mergedSeq = obj.last_event_seq;
  }

  const stored: StoredObject = {
    data: mergedData,
    meta: {
      lastSeq: mergedSeq,
      fetchedAt: Date.now(),
      isComplete: true,
    },
  };

  // Update PK index
  updatePkIndex(mergedData);

  // Refresh email index from the merged (authoritative) data. Full fetches
  // include the entity's complete `ahoy_email` array, so the index is now
  // consistent with the entity.
  _reindexEntityEmails(
    obj.entity_id,
    mergedData.entity_type_name,
    getEntityEmails(mergedData)
  );

  commitObjectMutation(obj.entity_id, stored);
  // Dev warning: log once per org session when store grows past a reasonable threshold.
  // This can indicate memory leaks or missing clearAll() calls on org switches.
  if (!sizeWarnEmitted && store.getSnapshot().size > 10_000) {
    sizeWarnEmitted = true;
    console.warn(
      `[crmObjectStore] Store size exceeded 10,000 entries (current: ${store.getSnapshot().size}). ` +
        'Consider calling clearAll() on org switch to prevent memory buildup.'
    );
  }
}

/**
 * Restore a previously-captured StoredObject (data + meta), bypassing the
 * seq guard.
 *
 * Use this for rollback after a failed optimistic update: the snapshot you
 * captured before the optimistic write contains the original meta, including
 * isComplete. Restoring via this path preserves whether the entry was a
 * complete (full-fetch) or summary (list-fetch) entry, so detail hooks
 * continue to fall back correctly to React Query for incomplete entries.
 *
 * ONLY use this for error recovery scenarios. For normal updates, use
 * upsertObject or upsertObjects which respect seq ordering.
 */
export function restoreStoredObject(stored: StoredObject): void {
  // Update PK index
  updatePkIndex(stored.data);

  // Refresh the email index from the restored data so a failed optimistic
  // update that mutated `ahoy_email` doesn't leave stale email-to-entity
  // resolutions behind. `_reindexEntityEmails` removes prior entries that
  // are not in the restored email set and re-adds the originals.
  _reindexEntityEmails(
    stored.data.entity_id,
    stored.data.entity_type_name,
    getEntityEmails(stored.data)
  );

  commitObjectMutation(stored.data.entity_id, stored);
}

/**
 * Remove an object entry from the store entirely.
 *
 * Used for rollback of optimistic writes against entities that did not
 * exist in the store before the mutation. Without this, a failed optimistic
 * update creates a partial entry with the optimistic seq/property that later
 * refetches keep merging (since upsertSummaryObjects' seq guard skips
 * server data with an older seq than the orphaned partial entry).
 */
export function removeObject(timelineId: string): void {
  const existing = store.getRecord(timelineId);
  if (!existing) {
    return;
  }

  // Drop any PK→timeline mapping that points at this entry so refetches by
  // numeric PK don't resolve back to a stale, removed entity.
  const pkStr = String(existing.data.id);
  if (pkStr !== timelineId) {
    if (store.isBatching()) {
      if (!batchPkIndex) {
        throw new Error('Batch state inconsistency');
      }
      if (batchPkIndex.get(pkStr) === timelineId) {
        batchPkIndex.delete(pkStr);
      }
    } else if (pkIndex.get(pkStr) === timelineId) {
      pkIndex = new Map(pkIndex);
      pkIndex.delete(pkStr);
    }
  }

  // Drop email-index entries for the removed entity so a rollback that
  // removes an optimistically-created object also clears its email
  // resolutions.
  _unindexEntityEmails(timelineId);

  store.remove(timelineId);
}

/**
 * Upsert multiple objects from a list or batch fetch.
 * Batches notifications — single emitChange at end.
 *
 * When the store has a newer seq for an object (from WS events), merges:
 * the server response provides the complete base, WS property updates
 * are overlaid on top. See upsertObject for rationale.
 */
export function upsertObjects(
  objs: CrmObject[],
  projectedTypes?: string[] | null
): void {
  if (objs.length === 0) {
    return;
  }

  const projection = toProjectedTypesSet(projectedTypes);

  beginBatch();
  try {
    for (const incoming of objs) {
      const existing = store.getRecord(incoming.entity_id);
      const obj = withGuaranteedId(incoming, existing?.data.id);

      let mergedData: CrmObject;
      let mergedSeq: number;

      if (existing && obj.last_event_seq < existing.meta.lastSeq) {
        // Store has newer seq — merge server base with WS property overlays.
        // Existing's associations also reflect WS-applied changes that this
        // older fetch hasn't seen.
        mergedData = {
          ...obj,
          associations: existing.data.associations,
          associated_objects: existing.data.associated_objects,
          properties: { ...obj.properties, ...existing.data.properties },
          last_event_seq: existing.meta.lastSeq,
        };
        mergedSeq = existing.meta.lastSeq;
      } else if (existing) {
        // Always merge against the existing entry — see `upsertObject` for
        // the full rationale. Critical for batch upserts because this is the
        // path the slideout's `queryFn` uses for `associated_entities`,
        // whose backend payloads are served with `skip_associations=True`
        // and therefore arrive with `obj.associations === undefined`. The
        // `undefined` guard prevents the spread from overwriting existing's
        // associations with undefined and visibly clearing
        // association-derived columns in the table behind the slideout. When
        // a projection is supplied, the incoming payload is only
        // authoritative for the projected types — see
        // `applyAssociationProjection`.
        mergedData = {
          ...obj,
          associations:
            obj.associations === undefined
              ? existing.data.associations
              : applyAssociationProjection(
                  existing.data.associations,
                  obj.associations,
                  projection
                ),
          // associated_objects: preserve a previously-hydrated overlay only
          // when this payload didn't opt in (`undefined`) — see `upsertObject`
          // for the full rationale.
          associated_objects:
            obj.associated_objects ?? existing.data.associated_objects,
          properties: { ...existing.data.properties, ...obj.properties },
        };
        mergedSeq = obj.last_event_seq;
      } else {
        // No existing entry — use new directly.
        mergedData = obj;
        mergedSeq = obj.last_event_seq;
      }

      const stored: StoredObject = {
        data: mergedData,
        meta: {
          lastSeq: mergedSeq,
          fetchedAt: Date.now(),
          isComplete: true,
        },
      };

      updatePkIndex(mergedData);
      store.commit(obj.entity_id, stored);
      if (reverseAliases.has(obj.entity_id)) {
        confirmedAliasTargets.add(obj.entity_id);
      }
    }
  } finally {
    endBatch();
  }
}

/**
 * Upsert multiple objects as **summary/partial** entries (isComplete: false).
 *
 * Used for associated objects from list responses, which only contain a handful
 * of display properties (display_name, domain, email, etc.). Storing them as
 * incomplete means:
 *   - subscribeToObject() returns undefined for these → slideout falls through
 *     to its own full API fetch via React Query
 *   - If the store already has a *complete* entry for the object (from a prior
 *     full fetch or detail page), we skip — never downgrade complete → incomplete
 *   - WS events that arrive later can still merge into these partial entries
 */
export function upsertSummaryObjects(
  objs: CrmObject[],
  projectedTypes?: string[] | null
): void {
  if (objs.length === 0) {
    return;
  }

  const projection = toProjectedTypesSet(projectedTypes);

  beginBatch();
  try {
    for (const incoming of objs) {
      const existing = store.getRecord(incoming.entity_id);
      const obj = withGuaranteedId(incoming, existing?.data.id);

      if (existing) {
        // Always merge against the existing entry — never skip outright on
        // equal-or-older seq, never replace the entry wholesale.
        //
        // Merge rules:
        //   - properties: union of both sides' keys. For overlapping keys,
        //     the side carrying the higher seq wins. (At equal seq the two
        //     responses come from the same logical state and agree on shared
        //     keys, so the merge is a no-op for them; the union still pulls
        //     in keys that are present on one side but not the other.)
        //   - associations: response wins when it carries the field AND is
        //     at-or-newer; otherwise existing is preserved (the projection is
        //     ignored entirely in that case). Association payloads are
        //     authoritative for the complete set ONLY when no `projectedTypes`
        //     is supplied — replace wholesale. When a projection is supplied,
        //     the response is only authoritative for those types: entries
        //     outside the projection are kept from existing, entries inside
        //     it come from the response (a removal inside the projection
        //     still takes effect). See `applyAssociationProjection`. Any
        //     future store write fed from a projected list request must pass
        //     its projection here.
        //   - deleted_at: response wins when at-or-newer; otherwise existing
        //     is preserved (don't undo a more recent soft-delete with a stale
        //     refetch).
        //   - lastSeq: max(existing, incoming).
        //   - isComplete: preserved. Never downgrade complete → incomplete,
        //     and never upgrade incomplete → complete from a summary write
        //     (a summary by definition lacks the full property set).
        //
        // Why merge unconditionally instead of skipping on equal seq:
        //   The `upsertObjects` path used by `single-slideout.tsx`'s
        //   `associated_entities` write stamps lean payloads (only display
        //   properties; `skip_associations=true`) as `isComplete: true`.
        //   A subsequent list refetch with the same `last_event_seq` used to
        //   be dropped here, leaving the store entry permanently missing the
        //   columns the table needs. `enrichedResults` (hooks.ts:783-793)
        //   then surfaced the lean entry as soon as any WS event bumped
        //   `stored.lastSeq` past the list response's `last_event_seq` —
        //   table cells visibly emptied for any company first seen via a
        //   contact slideout's `associated_entities`. Merging when the entry
        //   already exists pulls the list response's full property set into
        //   the store regardless of the entry's `isComplete` flag or seq
        //   direction, so `enrichedResults` returns rich data either way.
        const incomingIsAtLeastAsFresh =
          obj.last_event_seq >= existing.meta.lastSeq;

        const mergedProperties = incomingIsAtLeastAsFresh
          ? { ...existing.data.properties, ...obj.properties }
          : { ...obj.properties, ...existing.data.properties };

        // Associations: response is authoritative ONLY when it carries the
        // field AND is at-or-newer. Otherwise preserve existing (and ignore
        // the projection entirely — it only applies to an at-least-as-fresh
        // response).
        let mergedAssociations = existing.data.associations;
        if (obj.associations !== undefined && incomingIsAtLeastAsFresh) {
          mergedAssociations = applyAssociationProjection(
            existing.data.associations,
            obj.associations,
            projection
          );
        }

        let mergedAssociatedObjects = existing.data.associated_objects;
        if (
          obj.associated_objects !== undefined &&
          Object.keys(obj.associated_objects).length > 0 &&
          incomingIsAtLeastAsFresh
        ) {
          mergedAssociatedObjects = obj.associated_objects;
        }

        const mergedDeletedAt = incomingIsAtLeastAsFresh
          ? obj.deleted_at
          : existing.data.deleted_at;

        const mergedSeq = Math.max(obj.last_event_seq, existing.meta.lastSeq);

        const mergedData: CrmObject = {
          ...existing.data,
          ...obj,
          associations: mergedAssociations,
          associated_objects: mergedAssociatedObjects,
          properties: mergedProperties,
          last_event_seq: mergedSeq,
          deleted_at: mergedDeletedAt,
        };

        const updated: StoredObject = {
          data: mergedData,
          meta: {
            ...existing.meta,
            lastSeq: mergedSeq,
          },
        };

        // Update PK index when the merge revealed a numeric PK that the
        // existing entry didn't expose (e.g. existing was a partial entry
        // created by `applyPropertyUpdate` where id === entity_id).
        updatePkIndex(mergedData);
        store.commit(obj.entity_id, updated);
        if (reverseAliases.has(obj.entity_id)) {
          confirmedAliasTargets.add(obj.entity_id);
        }
        continue;
      }

      // No existing entry — create a fresh summary entry (isComplete: false).
      const stored: StoredObject = {
        data: obj,
        meta: {
          lastSeq: obj.last_event_seq,
          fetchedAt: Date.now(),
          isComplete: false,
        },
      };

      updatePkIndex(obj);
      store.commit(obj.entity_id, stored);
      if (reverseAliases.has(obj.entity_id)) {
        confirmedAliasTargets.add(obj.entity_id);
      }
    }
  } finally {
    endBatch();
  }
}

// ---------------------------------------------------------------------------
// Writes — from WebSocket events
// ---------------------------------------------------------------------------

/**
 * Apply a property update from a WebSocket event.
 *
 * Seq-guarded with three outcomes:
 * - 'applied': Properties merged into existing (or new partial entry created)
 * - 'skipped': Event is stale (seq <= stored seq)
 * - 'gap': Events were missed (seq > stored seq + 1) — caller should invalidate
 *
 * If the object isn't in the store, creates a partial entry (isComplete: false)
 * with only the changed properties. Hooks should fall back to React Query data
 * for incomplete entries.
 */
export function applyPropertyUpdate(
  timelineId: string,
  timelineTypeName: EntityTypeName,
  properties: Record<string, PropertyValue>,
  seq: number,
  orgId?: number
): SeqResult {
  const existing = store.getRecord(timelineId);

  if (existing) {
    const seqResult = store.checkSeq(existing.meta.lastSeq, seq);
    if (seqResult !== 'applied') {
      return seqResult;
    }

    // Merge properties into existing object
    const updatedData: CrmObject = {
      ...existing.data,
      properties: { ...existing.data.properties, ...properties },
      last_event_seq: seq,
    };

    const stored: StoredObject = {
      data: updatedData,
      meta: {
        ...existing.meta,
        lastSeq: seq,
      },
    };

    // Re-index emails when ahoy_email is present in the property update —
    // the event payload carries the full new value (the projection layer
    // replaces the email array atomically, not incrementally). For other
    // properties the index doesn't change.
    if ('ahoy_email' in properties) {
      _reindexEntityEmails(
        timelineId,
        updatedData.entity_type_name,
        getEntityEmails(updatedData)
      );
    }

    commitObjectMutation(timelineId, stored);
    return 'applied';
  }

  // Object not in store — create a partial entry.
  // This happens when a WS event arrives for an object we haven't fetched.
  const partialData: CrmObject = {
    id: timelineId,
    org_id: orgId ?? 0, // From caller context; 0 if unavailable (partial entry is guarded by isComplete)
    entity_id: timelineId,
    entity_type_name: timelineTypeName,
    properties,
    last_event_seq: seq,
    associations: [],
    deleted_at: null,
  };

  const stored: StoredObject = {
    data: partialData,
    meta: {
      lastSeq: seq,
      fetchedAt: 0,
      isComplete: false,
    },
  };

  // Index emails from this partial entry. If the event's properties don't
  // include ahoy_email this is a no-op; otherwise subscribers waiting on
  // those addresses can resolve immediately.
  if ('ahoy_email' in properties) {
    _reindexEntityEmails(
      timelineId,
      partialData.entity_type_name,
      getEntityEmails(partialData)
    );
  }

  commitObjectMutation(timelineId, stored);
  return 'applied';
}

/**
 * Apply an association add/remove from a WebSocket event.
 *
 * Seq-guarded. If the object isn't in the store, this is a no-op
 * (no entry to mutate; the eventual list refetch will populate it).
 *
 * Adding an existing association is idempotent — duplicates are not appended.
 * Removing a non-existent association is a no-op against the array, but seq
 * still advances to keep dedup correct.
 *
 * Why this exists: prior to this, `applyEvent.ts` deferred all association
 * writes to a debounced refetch on the rationale that associations were
 * "structural changes that can't be applied incrementally". That's incorrect
 * — appending or removing an entry from `associations` is purely incremental
 * — and the deferral broke real-time updates of association-derived UI
 * (lifecycle stage cells, deal pipeline columns, etc.) because the refetch
 * path also drops new data through `upsertSummaryObjects` when an existing
 * complete entry was already in the store.
 */
export function applyAssociationChange(
  timelineId: string,
  action: 'added' | 'removed',
  associationTypeName: string,
  targetEntityId: string,
  seq: number,
  options?: { forceNewReference?: boolean }
): SeqResult {
  const existing = store.getRecord(timelineId);
  if (!existing) {
    // No entry in the store — let the debounced list refetch populate it.
    // We can't synthesize a partial entry here without a known entity_type,
    // and the cross-entity invalidation in applyEvent.ts is sufficient.
    return 'skipped';
  }

  const seqResult = store.checkSeq(existing.meta.lastSeq, seq);
  if (seqResult !== 'applied') {
    return seqResult;
  }

  const currentAssociations = existing.data.associations ?? [];
  let nextAssociations: typeof currentAssociations;

  if (action === 'added') {
    // Idempotent at the value level: a (type, target) pair is never duplicated.
    const alreadyPresent = currentAssociations.some(
      a =>
        a.association_type_name === associationTypeName &&
        a.target_entity_id === targetEntityId
    );
    if (!alreadyPresent) {
      nextAssociations = [
        ...currentAssociations,
        {
          association_type_name: associationTypeName,
          target_entity_id: targetEntityId,
        },
      ];
    } else if (options?.forceNewReference) {
      // Value-idempotent, but swap in a FRESH element reference. An in-flight
      // optimistic rollback distinguishes "our optimistic add is still live"
      // from "an authoritative event re-asserted this pair" purely by
      // AssociationDetail reference identity (see revertAssociations →
      // `working.includes(addedRef)`). Preserving the existing element on an
      // idempotent re-add kept that identity check true, so the rollback
      // stripped a pair a concurrent write had just re-confirmed. Both the
      // authoritative path (WS delivery) AND the optimistic batch path pass
      // forceNewReference so the re-add replaces the element and an older
      // in-flight batch's rollback correctly keeps the newer write.
      nextAssociations = currentAssociations.map(a =>
        a.association_type_name === associationTypeName &&
        a.target_entity_id === targetEntityId
          ? { ...a }
          : a
      );
    } else {
      nextAssociations = currentAssociations;
    }
  } else {
    nextAssociations = currentAssociations.filter(
      a =>
        !(
          a.association_type_name === associationTypeName &&
          a.target_entity_id === targetEntityId
        )
    );
  }

  const updatedData: CrmObject = {
    ...existing.data,
    associations: nextAssociations,
    last_event_seq: seq,
  };

  const stored: StoredObject = {
    data: updatedData,
    meta: {
      ...existing.meta,
      lastSeq: seq,
    },
  };

  commitObjectMutation(timelineId, stored);
  return 'applied';
}

/**
 * Atomically point a single-valued association (a pipeline stage, a lifecycle
 * stage) at `targetEntityId`, dropping every other association of that type.
 *
 * Use this instead of `applyAssociationChange('removed', …)` followed by
 * `applyAssociationChange('added', …)`. Those are guarded independently, so the
 * pair is NOT atomic: if the stored seq has advanced past the remove's seq but
 * not the add's, the remove is dropped and the add lands, leaving the object
 * holding TWO stage associations. Readers that resolve a stage with
 * `associations.find(...)` then place the record in an arbitrary,
 * order-dependent column — a card appearing in a stage it isn't in, which a
 * refetch silently "fixes", making it look like a rendering glitch rather than
 * corrupt local state.
 *
 * One seq check, one commit: either the whole swap applies or none of it does.
 */
export function replaceSingleAssociation(
  timelineId: string,
  associationTypeName: string,
  targetEntityId: string,
  seq: number
): SeqResult {
  const existing = store.getRecord(timelineId);
  if (!existing) {
    return 'skipped';
  }

  const seqResult = store.checkSeq(existing.meta.lastSeq, seq);
  if (seqResult !== 'applied') {
    return seqResult;
  }

  const currentAssociations = existing.data.associations ?? [];
  const nextAssociations = [
    ...currentAssociations.filter(
      a => a.association_type_name !== associationTypeName
    ),
    {
      association_type_name: associationTypeName,
      target_entity_id: targetEntityId,
    },
  ];

  const stored: StoredObject = {
    data: {
      ...existing.data,
      associations: nextAssociations,
      last_event_seq: seq,
    },
    meta: { ...existing.meta, lastSeq: seq },
  };

  commitObjectMutation(timelineId, stored);
  return 'applied';
}

/**
 * Mark an object as deleted from a WebSocket event.
 *
 * Seq-guarded. If the object isn't in the store, this is a no-op
 * (we can't mark something deleted if we don't have it).
 */
export function applyObjectDeleted(
  timelineId: string,
  seq: number,
  deletedAt: string
): SeqResult {
  const existing = store.getRecord(timelineId);
  if (!existing) {
    return 'skipped';
  }

  const seqResult = store.checkSeq(existing.meta.lastSeq, seq);
  if (seqResult !== 'applied') {
    return seqResult;
  }

  const updatedData: CrmObject = {
    ...existing.data,
    deleted_at: deletedAt,
    last_event_seq: seq,
  };

  const stored: StoredObject = {
    data: updatedData,
    meta: {
      ...existing.meta,
      lastSeq: seq,
    },
  };

  // Soft-deleted entities should not resolve from the email index — drop
  // their entries so `usePersonByEmail` (and any other email-keyed reader)
  // stops returning them.
  _unindexEntityEmails(timelineId);

  commitObjectMutation(timelineId, stored);
  return 'applied';
}

/**
 * Apply a merge event from a WebSocket `ahoy_object_merged` payload.
 *
 * Effects:
 *   - Registers an alias from the source entity id to the canonical id, so
 *     callers that hold the pre-merge id (e.g. an association target that
 *     hasn't refetched yet) resolve to the canonical entry via
 *     {@link resolveEntityId}.
 *   - Transfers every email currently indexed against the source to the
 *     canonical, so {@link usePersonByEmail} consumers re-render with the
 *     canonical entity immediately (no React Query invalidation round-trip).
 *
 * Does NOT mutate the source's `CrmObject` — that's left to whatever
 * refetch the caller (typically `applyEvent.ts`'s merge handler) schedules
 * for downstream cache layers. The store's job here is to redirect lookups.
 */
export function applyObjectMerged(sourceId: string, canonicalId: string): void {
  if (!sourceId || !canonicalId || sourceId === canonicalId) {
    return;
  }
  registerEntityAlias(sourceId, canonicalId);
  _transferEntityEmails(sourceId, canonicalId);
  store.touch(canonicalId);
  // `registerEntityAlias` triggers a store commit (when the canonical
  // exists) so generic subscribers re-resolve via `resolveEntityId`;
  // `_transferEntityEmails` emits per-email changes for callers subscribed
  // via `subscribeToEmailIndex`.
}

// ---------------------------------------------------------------------------
// Lifecycle
// ---------------------------------------------------------------------------

/**
 * Clear all store state. Called on org switch.
 */
export function clearAll(): void {
  // Bump the generation unconditionally — even when the store is already
  // empty, a clear marks an org-lifecycle boundary that in-flight async
  // writers must treat as a cancellation point.
  storeGeneration += 1;
  sizeWarnEmitted = false;
  // Include the email-index caches in the empty-state check — they can hold
  // negative resolutions and entity↔email mappings even when no full objects
  // have been hydrated (e.g. a session that only resolved emails through
  // batch lookups). Leaving them behind across org switches would leak stale
  // unresolved-email state into the new org's resolution paths.
  if (
    store.getSnapshot().size === 0 &&
    pendingCreations.size === 0 &&
    !store.isBatching() &&
    emailIndex.size === 0 &&
    emailIndexByEntityId.size === 0 &&
    emailIndexNegative.size === 0 &&
    emailIndexSnapshots.size === 0
  ) {
    return;
  }

  // Drain (rather than discard) any in-progress batch. This commits
  // whatever was pending into the store, which the removal pass below then
  // wipes along with everything else — net effect matches "abort the
  // batch" (nothing from it survives) while going through the public
  // remove() API so active subscriptions below aren't disturbed.
  while (store.isBatching()) {
    endBatch();
  }

  // Clear pending creations and associations
  if (pendingCreations.size > 0) {
    for (const timer of pendingCreationTimers.values()) {
      clearTimeout(timer);
    }
    pendingCreationTimers = new Map();
    pendingCreations = new Set();
    pendingCreationsSnapshot = pendingCreations;
    pendingAssociations = new Map();
    emitPendingCreationsChange();
  }

  // Remove every stored record through the public API (rather than
  // store.reset()) so active per-record/global subscriptions survive the
  // org switch — components stay mounted across org switches and must be
  // notified their data is gone, not silently disconnected from future
  // updates for the new org. Batched so this is a single Map swap and a
  // single round of notifications, not one per removed record.
  const idsToRemove = [...store.getSnapshot().keys()];
  if (idsToRemove.length > 0) {
    beginBatch();
    for (const id of idsToRemove) {
      store.remove(id);
    }
    endBatch();
  }

  pkIndex = new Map();
  aliases = new Map();
  reverseAliases = new Map();
  confirmedAliasTargets = new Set();
  // Reset the email index alongside the entity store — both are sourced
  // from the same apply* events and have the same per-org lifecycle.
  const previouslyWatchedEmails = [...emailIndexListeners.keys()];
  emailIndex = new Map();
  emailIndexByEntityId = new Map();
  emailIndexNegative = new Set();
  emailIndexSnapshots = new Map();
  // Notify per-email listeners — they should re-resolve against the empty index
  for (const email of previouslyWatchedEmails) {
    emitEmailIndexChange(email);
  }
}

/**
 * Reset all state for testing. Only exported for test use.
 */
export function __resetForTesting(): void {
  store.reset();
  pkIndex = new Map();
  aliases = new Map();
  reverseAliases = new Map();
  confirmedAliasTargets = new Set();
  subscribedIds = new Map();
  emailIndex = new Map();
  emailIndexByEntityId = new Map();
  emailIndexNegative = new Set();
  emailIndexSnapshots = new Map();
  emailIndexListeners = new Map();
  sizeWarnEmitted = false;
  storeGeneration = 0;
  batchPkIndex = null;
  batchChangedEmails = new Set();
  pendingCreations = new Set();
  pendingCreationsSnapshot = pendingCreations;
  pendingCreationsListeners = new Set();
  pendingAssociations = new Map();
  for (const timer of pendingCreationTimers.values()) {
    clearTimeout(timer);
  }
  pendingCreationTimers = new Map();
  currentMaxEntries = STORE_MAX_ENTRIES;
  previewOnlyIdGuards = new Set();
}

/** Test-only override for the eviction cap. */
export function __setMaxEntriesForTesting(n: number): void {
  currentMaxEntries = n;
  store.setMaxEntries(n);
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/**
 * Commit a mutation to a single object via the toolkit, then run the
 * CRM-specific bookkeeping that has to happen alongside every write:
 * marking a confirmed alias target (see `confirmedAliasTargets`) and
 * sweeping any CRM-side state an LRU eviction may have just orphaned.
 */
function commitObjectMutation(timelineId: string, stored: StoredObject): void {
  store.commit(timelineId, stored);
  if (reverseAliases.has(timelineId)) {
    confirmedAliasTargets.add(timelineId);
  }
  _maybeReconcileEvictedCrmState();
}

/**
 * Update the PK index when an object is stored.
 * Maps String(object.id) → timeline_id, but only if id differs from timeline_id
 * (when id is a numeric PK string, not the UUID).
 *
 * Batch-aware: during a batch, mutates the working copy in-place instead of
 * creating a new Map per call.
 */
function updatePkIndex(obj: CrmObject): void {
  const pkStr = String(obj.id);
  if (pkStr !== obj.entity_id) {
    if (store.isBatching()) {
      if (!batchPkIndex) {
        throw new Error('Batch state inconsistency');
      }
      batchPkIndex.set(pkStr, obj.entity_id);
    } else {
      pkIndex = new Map(pkIndex);
      pkIndex.set(pkStr, obj.entity_id);
    }
  }
}

/** Notify per-email listeners for a specific normalized email. */
function emitEmailIndexChange(normalizedEmail: string): void {
  const set = emailIndexListeners.get(normalizedEmail);
  if (!set) {
    return;
  }
  for (const listener of set) {
    listener();
  }
}

/**
 * Normalize an email for the index: lowercase + trim. Same rule used by
 * the backend's `normalize_and_hash_email`, so callers don't need to
 * pre-normalize. Empty strings yield `''` — callers should treat that
 * as "no email" and skip indexing.
 */
function _normalizeEmail(raw: unknown): string {
  if (typeof raw !== 'string') {
    return '';
  }
  return raw.trim().toLowerCase();
}

/**
 * Pull every `ahoy_email` value off a CrmObject's properties as a list of
 * normalized strings. The schema declares `ahoy_email` as `is_multi: true`,
 * so the property value can be either a single string or an array of
 * strings depending on the serialization path. This helper accepts either.
 *
 * SINGLE SOURCE OF TRUTH for email-property extraction in the frontend.
 * Any code that needs to read emails off a CrmObject MUST go through this
 * helper (or `getEntityPrimaryEmail`) — never re-implement the
 * `Array.isArray + Array.filter + lowercase` shuffle inline. The
 * store's email index depends on this exact normalization rule, so
 * inline re-implementations drift and break lookups.
 */
export function getEntityEmails(obj: CrmObject): string[] {
  const prop = obj.properties?.ahoy_email;
  if (!prop) {
    return [];
  }
  const raw = prop.value;
  const arr = Array.isArray(raw) ? raw : [raw];
  const out: string[] = [];
  for (const item of arr) {
    const normalized = _normalizeEmail(item);
    if (normalized) {
      out.push(normalized);
    }
  }
  return out;
}

/**
 * Convenience wrapper around `getEntityEmails` for callers that only need
 * the primary email (display chips, modal pre-fill, etc.). Returns
 * `undefined` when the entity has no email set — never an empty string.
 */
export function getEntityPrimaryEmail(obj: CrmObject): string | undefined {
  const emails = getEntityEmails(obj);
  return emails[0] ?? undefined;
}

/**
 * Email-resolution priority by entity type, lower rank = preferred winner.
 *
 * Mirrors the server-side `_pickBestMatch` order in `usePersonByEmail`
 * (user > contact): when more than one entity carries the same email, the
 * canonical user/contact must win. Person types not in this map (and
 * non-person types that happen to carry an email) get a middle rank so they
 * neither clobber a real person nor are clobbered by an unknown-typed
 * entry; truly unknown (un-stored) winners get the lowest priority so a
 * known person type takes over them.
 */
const EMAIL_RESOLUTION_RANK: Record<string, number> = {
  [USER]: 0,
  [CONTACT]: 1,
};
const EMAIL_RESOLUTION_RANK_OTHER = 50;
const EMAIL_RESOLUTION_RANK_UNKNOWN = 90;

function _emailResolutionRank(entityType: string | undefined): number {
  if (entityType === undefined) {
    return EMAIL_RESOLUTION_RANK_UNKNOWN;
  }
  return EMAIL_RESOLUTION_RANK[entityType] ?? EMAIL_RESOLUTION_RANK_OTHER;
}

/**
 * Of two entities that resolve to the same email, return the one that should
 * win for display: user > contact (see `EMAIL_RESOLUTION_RANK`). Ties keep
 * `existing`, so callers that iterate a candidate list get first-write-wins
 * within a rank.
 *
 * Exported for association-driven resolvers (e.g. the meeting-attendee
 * `contactsByEmail` map) so they pick the SAME canonical entity the store's
 * email index does — keeping every email→entity resolution surface
 * consistent with the server-side `_pickBestMatch` ordering.
 */
export function preferResolvedEmailEntity(
  existing: CrmObject | undefined,
  candidate: CrmObject
): CrmObject {
  if (existing === undefined) {
    return candidate;
  }
  return _emailResolutionRank(candidate.entity_type_name) <
    _emailResolutionRank(existing.entity_type_name)
    ? candidate
    : existing;
}

/**
 * Look up the stored entity_type_name for an entity id, batch-aware. Returns
 * `undefined` when the entity is not (yet) committed to the store — e.g. the
 * entity currently being upserted, whose commit happens AFTER its reindex,
 * which is why `_reindexEntityEmails` takes the type as an explicit argument
 * rather than reading its own type back from the store.
 */
function _storedEntityType(entityId: string): string | undefined {
  return store.getRecord(entityId)?.data.entity_type_name;
}

/**
 * Index/re-index the email entries for an entity. Removes any stale entries
 * (emails previously associated with this entity that aren't in the new
 * set) and adds the new entries. Emits change events for every affected
 * email so subscribers see the update.
 *
 * `entityType` is the entity_type_name of the entity being (re)indexed. It is
 * passed in (not read from the store) because callers invoke this BEFORE
 * committing the object, so `_storedEntityType(entityId)` would miss it. It is
 * used to resolve same-email collisions by `_emailResolutionRank` instead of
 * the prior blind last-write-wins, so a lower-ranked entity can no longer
 * overwrite the canonical user/contact for a shared email.
 */
function _reindexEntityEmails(
  entityId: string,
  entityType: string | undefined,
  currentEmails: string[]
): void {
  const previous = emailIndexByEntityId.get(entityId) ?? new Set<string>();
  const currentSet = new Set(currentEmails);
  const changed = new Set<string>();

  // Remove stale entries.
  for (const email of previous) {
    if (!currentSet.has(email)) {
      if (emailIndex.get(email) === entityId) {
        emailIndex.delete(email);
        emailIndexSnapshots.delete(email);
        changed.add(email);
      }
    }
  }

  // Add new entries, resolving same-email collisions by entity-type priority
  // (`_emailResolutionRank`) rather than blind last-write-wins. A lower-or-equal
  // rank wins: a canonical user/contact takes over a lower-ranked entity
  // regardless of load order, and a later same-type write still wins on ties
  // (preserving last-write-wins for merges and re-fetches of the same entity).
  // This keeps the store's resolution consistent with the server-side
  // `_pickBestMatch` order so a chip never resolves to a stale lower-ranked
  // entity when the real user/contact for that email is also in the store.
  const candidateRank = _emailResolutionRank(entityType);
  for (const email of currentSet) {
    const currentWinner = emailIndex.get(email);
    if (currentWinner !== entityId) {
      const candidateWins =
        currentWinner === undefined ||
        candidateRank <= _emailResolutionRank(_storedEntityType(currentWinner));
      if (candidateWins) {
        emailIndex.set(email, entityId);
        emailIndexSnapshots.delete(email);
        changed.add(email);
      }
    }
    // Any apply that touches a known email clears the negative cache for
    // it — subscribers waiting on `email → null` should re-resolve.
    if (emailIndexNegative.has(email)) {
      emailIndexNegative.delete(email);
      emailIndexSnapshots.delete(email);
      changed.add(email);
    }
  }

  if (currentSet.size === 0) {
    emailIndexByEntityId.delete(entityId);
  } else {
    emailIndexByEntityId.set(entityId, currentSet);
  }

  // Emit per-email change events outside the batch path. Inside a batch,
  // accumulate changes and flush them at endBatch — the toolkit's global
  // change notification only notifies generic subscribers, not callers that
  // subscribed to a specific email through `subscribeToEmailIndex`.
  if (!store.isBatching()) {
    for (const email of changed) {
      emitEmailIndexChange(email);
    }
  } else {
    for (const email of changed) {
      batchChangedEmails.add(email);
    }
  }
}

/**
 * Drop every email entry pointing at an entity (e.g. on delete or as part
 * of a merge transfer). Emits change events so subscribers re-resolve.
 */
function _unindexEntityEmails(entityId: string): void {
  const previous = emailIndexByEntityId.get(entityId);
  if (!previous || previous.size === 0) {
    return;
  }
  for (const email of previous) {
    if (emailIndex.get(email) === entityId) {
      emailIndex.delete(email);
      emailIndexSnapshots.delete(email);
      if (!store.isBatching()) {
        emitEmailIndexChange(email);
      } else {
        batchChangedEmails.add(email);
      }
    }
  }
  emailIndexByEntityId.delete(entityId);
}

/**
 * Re-point every email currently indexed against `sourceId` to `canonicalId`.
 * Used when the source entity is merged into the canonical — subscribers
 * watching the email then resolve to the canonical entity automatically.
 */
function _transferEntityEmails(sourceId: string, canonicalId: string): void {
  const previous = emailIndexByEntityId.get(sourceId);
  if (!previous || previous.size === 0) {
    return;
  }

  const existingCanonicalEmails =
    emailIndexByEntityId.get(canonicalId) ?? new Set<string>();
  for (const email of previous) {
    if (emailIndex.get(email) === sourceId) {
      emailIndex.set(email, canonicalId);
      emailIndexSnapshots.delete(email);
      if (!store.isBatching()) {
        emitEmailIndexChange(email);
      } else {
        batchChangedEmails.add(email);
      }
    }
    existingCanonicalEmails.add(email);
  }
  emailIndexByEntityId.delete(sourceId);
  emailIndexByEntityId.set(canonicalId, existingCanonicalEmails);
}

// ---------------------------------------------------------------------------
// Dev tools — browser console access in development mode
// ---------------------------------------------------------------------------

if (import.meta.env.DEV) {
  (window as unknown as Record<string, unknown>).__crmStore = {
    getSnapshot,
    getObject,
    getObjectByPk,
    getObjectSeq,
    hasCompleteObject,
    /** Number of objects currently in the store. */
    get size() {
      return store.getSnapshot().size;
    },
    /** All timeline IDs in the store. */
    get keys() {
      return [...store.getSnapshot().keys()];
    },
    /** Dump all stored objects as a plain array (for quick inspection). */
    dump() {
      return [...store.getSnapshot().values()].map(s => ({
        entity_id: s.data.entity_id,
        type: s.data.entity_type_name,
        seq: s.meta.lastSeq,
        complete: s.meta.isComplete,
        properties: s.data.properties,
      }));
    },
  };
}
